import React, { useEffect, useState } from 'react';
import axios from 'axios';

const API_URL = process.env.REACT_APP_API_URL || 'http://localhost:3001/api';

const formatBytes = (bytes) => {
  if (bytes == null || bytes === 0) return '0 B';
  if (bytes >= 1073741824) return (bytes / 1073741824).toFixed(1) + ' GB';
  if (bytes >= 1048576) return (bytes / 1048576).toFixed(1) + ' MB';
  if (bytes >= 1024) return (bytes / 1024).toFixed(1) + ' KB';
  return bytes + ' B';
};

const Storage = () => {
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');

  useEffect(() => { fetchUsers(); }, []);

  const fetchUsers = async () => {
    try {
      const res = await axios.get(`${API_URL}/users`);
      setUsers(res.data); setError('');
    } catch (err) {
      setError(err.response?.data?.error || 'Failed to load users'); setUsers([]);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center min-h-[40vh]">
        <div className="w-12 h-12 rounded-2xl bg-violet-50 flex items-center justify-center">
          <div className="w-6 h-6 border-2 border-violet-600 border-t-transparent rounded-full animate-spin" />
        </div>
      </div>
    );
  }

  return (
    <div className="space-y-5">
      <div>
        <h1 className="text-xl font-bold text-slate-900">Storage Usage</h1>
        <p className="text-sm text-slate-500 mt-0.5">Allocated, consumed, and remaining space per user</p>
      </div>

      {error && (
        <div className="flex items-start gap-2.5 p-4 bg-rose-50 border border-rose-200 rounded-2xl">
          <svg className="w-4 h-4 text-rose-500 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 8v4m0 4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
          </svg>
          <p className="text-rose-700 text-sm">{error}</p>
        </div>
      )}

      {users.length === 0 ? (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm p-12 text-center">
          <p className="text-slate-500 text-sm">No users found.</p>
        </div>
      ) : (
        <div className="bg-white rounded-2xl border border-slate-200 shadow-sm overflow-hidden">
          <div className="overflow-x-auto">
            <table className="min-w-full">
              <thead>
                <tr className="border-b border-slate-100">
                  {['User', 'Email / Phone', 'Plan', 'Expires', 'Days left', 'Allowed', 'Used', 'Remaining', '%'].map((h, i) => (
                    <th key={h} className={`px-4 py-3 text-xs font-semibold text-slate-400 uppercase tracking-wide ${i >= 5 ? 'text-right' : 'text-left'}`}>{h}</th>
                  ))}
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-50">
                {users.map((u) => {
                  const quota = u.disk_quota_bytes != null ? Number(u.disk_quota_bytes) : null;
                  const consumed = Number(u.disk_consumed_bytes || 0);
                  const remaining = quota != null && quota > 0 ? Math.max(0, quota - consumed) : null;
                  const pct = quota != null && quota > 0 ? Math.round((consumed / quota) * 100) : null;
                  const expiresAt = u.subscription_expires_at ? new Date(u.subscription_expires_at) : null;
                  const today = new Date(); today.setHours(0, 0, 0, 0);
                  const daysLeft = expiresAt ? Math.ceil((expiresAt.getTime() - today.getTime()) / (24 * 60 * 60 * 1000)) : null;
                  return (
                    <tr key={u.id} className="hover:bg-slate-50 transition-colors">
                      <td className="px-4 py-3 text-sm font-semibold text-slate-800">{u.name || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{u.email || '—'}{u.phone ? ` · ${u.phone}` : ''}</td>
                      <td className="px-4 py-3 text-sm text-slate-600">{u.plan_display || '—'}</td>
                      <td className="px-4 py-3 text-sm text-slate-500">{expiresAt ? expiresAt.toLocaleDateString(undefined, { dateStyle: 'medium' }) : '—'}</td>
                      <td className="px-4 py-3 text-sm">
                        {daysLeft !== null ? (
                          <span className={`font-medium ${daysLeft < 0 ? 'text-rose-600' : daysLeft <= 7 ? 'text-amber-600' : 'text-slate-600'}`}>
                            {daysLeft < 0 ? `Expired ${Math.abs(daysLeft)}d ago` : `${daysLeft}d`}
                          </span>
                        ) : '—'}
                      </td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-violet-700">{quota != null && quota > 0 ? formatBytes(quota) : 'Unlimited'}</td>
                      <td className="px-4 py-3 text-sm text-right text-amber-700">{formatBytes(consumed)}</td>
                      <td className="px-4 py-3 text-sm text-right font-medium text-emerald-700">{remaining != null ? formatBytes(remaining) : '—'}</td>
                      <td className="px-4 py-3 text-right">
                        {pct != null ? (
                          <div className="flex items-center justify-end gap-2">
                            <div className="w-16 h-1.5 bg-slate-100 rounded-full overflow-hidden">
                              <div className={`h-1.5 rounded-full ${pct >= 90 ? 'bg-rose-500' : pct >= 70 ? 'bg-amber-400' : 'bg-emerald-500'}`} style={{ width: `${Math.min(pct, 100)}%` }} />
                            </div>
                            <span className={`text-xs font-medium ${pct >= 90 ? 'text-rose-600' : 'text-slate-600'}`}>{pct}%</span>
                          </div>
                        ) : '—'}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
};

export default Storage;
